import Phaser from 'phaser';
import { BaseScene } from '@core/scenes/BaseScene';
import { Palette, SceneKeys } from '@core/config/constants';
import { BACKGROUNDS, type BackgroundOption } from '@core/config/backgrounds';

const MAX_CARD_WIDTH = 230;

/**
 * Selector de escenarios: el jugador gasta estrellas para desbloquear fondos y equiparlos.
 * El fondo elegido se aplica a todas las pantallas de menu. Cada tarjeta muestra una vista
 * previa del escenario (degradado o imagen).
 */
export class ScenariosScene extends BaseScene {
  public constructor() {
    super(SceneKeys.Scenarios);
  }

  public create(): void {
    this.initResponsive();
    this.addSelectedBackground();
    this.addTwinkles(18);

    this.addBackButton();
    this.renderCoins();

    this.add
      .text(this.centerX, this.scale.height * 0.07, '🎬 Escenarios', {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '46px',
        color: Palette.textLight,
      })
      .setOrigin(0.5);

    this.add
      .text(this.centerX, this.scale.height * 0.07 + 42, 'Desbloqueá fondos y elegí tu favorito', {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '22px',
        color: '#4dd0e1',
      })
      .setOrigin(0.5);

    this.renderGrid(this.scale.height * 0.2);
  }

  private renderCoins(): void {
    this.add
      .text(this.scale.width - 28, 28, `⭐ ${this.profile.coins}`, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '30px',
        color: Palette.textLight,
      })
      .setOrigin(1, 0);
  }

  private renderGrid(gridTop: number): void {
    const { width, height } = this.scale;
    const gridBottom = height - 56;
    const gridAreaHeight = Math.max(160, gridBottom - gridTop);

    const gap = Math.max(16, width * 0.02);
    const idealColumnWidth = 210;
    let columns = Math.max(1, Math.floor((width * 0.92 + gap) / (idealColumnWidth + gap)));
    columns = Math.min(columns, BACKGROUNDS.length, 4);
    if (this.isPortrait) {
      columns = Math.min(columns, 2);
    }
    const rows = Math.ceil(BACKGROUNDS.length / columns);

    const cardWidth = Math.min(MAX_CARD_WIDTH, (width * 0.92 - (columns - 1) * gap) / columns);
    const cardHeight = Math.min(cardWidth * 1.05, (gridAreaHeight - (rows - 1) * gap) / rows);

    const totalHeight = rows * cardHeight + (rows - 1) * gap;
    const startY = gridTop + (gridAreaHeight - totalHeight) / 2 + cardHeight / 2;

    BACKGROUNDS.forEach((option, index) => {
      const column = index % columns;
      const row = Math.floor(index / columns);
      const cardsInRow = Math.min(columns, BACKGROUNDS.length - row * columns);
      const rowWidth = cardsInRow * cardWidth + (cardsInRow - 1) * gap;
      const startX = this.centerX - rowWidth / 2 + cardWidth / 2;

      const x = startX + column * (cardWidth + gap);
      const y = startY + row * (cardHeight + gap);
      this.createCard(option, x, y, cardWidth, cardHeight, index * 60);
    });
  }

  private createCard(
    option: BackgroundOption,
    x: number,
    y: number,
    cardWidth: number,
    cardHeight: number,
    entranceDelay: number,
  ): void {
    const owned = this.profile.ownsBackground(option.id);
    const equipped = this.profile.background === option.id;

    const container = this.add.container(x, y);

    const background = this.add.graphics();
    background.fillStyle(option.cardColor, owned ? 1 : 0.85);
    background.fillRoundedRect(-cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight, 20);
    background.lineStyle(5, Palette.highlight, equipped ? 1 : 0);
    background.strokeRoundedRect(-cardWidth / 2, -cardHeight / 2, cardWidth, cardHeight, 20);
    container.add(background);

    container.add(this.buildPreview(option, cardWidth, cardHeight));

    const name = this.add
      .text(0, cardHeight * 0.1, option.name, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '22px',
        color: Palette.textLight,
      })
      .setOrigin(0.5);

    const status = this.add
      .text(0, cardHeight * 0.33, this.statusLabel(option, owned, equipped), {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '20px',
        color: equipped ? '#1a1033' : Palette.textLight,
        backgroundColor: equipped ? '#ffd54f' : 'rgba(0,0,0,0.25)',
        padding: { x: 12, y: 6 },
      })
      .setOrigin(0.5);

    container.add([name, status]);
    container.setSize(cardWidth, cardHeight);
    container.setInteractive({ useHandCursor: true });

    container.setScale(0.7).setAlpha(0);
    this.tweens.add({
      targets: container,
      scale: 1,
      alpha: 1,
      duration: 280,
      delay: entranceDelay,
      ease: 'Back.easeOut',
    });

    container.on(Phaser.Input.Events.POINTER_OVER, () => container.setScale(1.05));
    container.on(Phaser.Input.Events.POINTER_OUT, () => container.setScale(1));
    container.on(Phaser.Input.Events.POINTER_UP, () => this.onCardTap(option));
  }

  /** Crea una mini vista previa del escenario dentro de la tarjeta. */
  private buildPreview(option: BackgroundOption, cardWidth: number, cardHeight: number): Phaser.GameObjects.GameObject {
    const previewWidth = cardWidth * 0.72;
    const previewHeight = cardHeight * 0.4;
    const previewY = -cardHeight * 0.22;

    if (option.background.type === 'gradient') {
      const preview = this.add.graphics();
      preview.fillGradientStyle(
        option.background.top,
        option.background.top,
        option.background.bottom,
        option.background.bottom,
        1,
      );
      preview.fillRoundedRect(-previewWidth / 2, previewY - previewHeight / 2, previewWidth, previewHeight, 12);
      const emoji = this.add
        .text(0, previewY, option.emoji, { fontSize: `${Math.round(previewHeight * 0.7)}px` })
        .setOrigin(0.5);
      const group = this.add.container(0, 0, [preview, emoji]);
      return group;
    }

    return this.add
      .text(0, previewY, option.emoji, { fontSize: `${Math.round(previewHeight * 0.9)}px` })
      .setOrigin(0.5);
  }

  private statusLabel(option: BackgroundOption, owned: boolean, equipped: boolean): string {
    if (equipped) {
      return 'Equipado ✓';
    }
    if (owned) {
      return 'Usar';
    }
    return `⭐ ${option.price}`;
  }

  private onCardTap(option: BackgroundOption): void {
    if (this.profile.ownsBackground(option.id)) {
      if (this.profile.background !== option.id) {
        this.profile.setBackground(option.id);
        this.audio.playClick();
        this.scene.restart();
      }
      return;
    }

    if (this.profile.buyBackground(option.id, option.price)) {
      this.profile.setBackground(option.id);
      this.audio.playReward();
      this.scene.restart();
      return;
    }

    this.audio.playWrong();
    this.showToast(`Te faltan estrellas para ${option.name}`);
  }

  private showToast(message: string): void {
    const toast = this.add
      .text(this.centerX, this.scale.height - 36, message, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '22px',
        color: '#ffffff',
        backgroundColor: 'rgba(255,45,142,0.9)',
        padding: { x: 16, y: 8 },
      })
      .setOrigin(0.5);

    this.tweens.add({
      targets: toast,
      alpha: 0,
      y: toast.y - 30,
      delay: 900,
      duration: 600,
      onComplete: () => toast.destroy(),
    });
  }
}
